import { HISTORY_ACTION_MODE, NodeId } from '@candulabs/craft';
import { pickBy } from 'lodash';
import { useMemo } from 'react';

import { useMultiNodes } from 'craft/hooks/useMultiNodes';
import { CssSelectorId, getCssSelectorDefinition } from 'craft/types';
import { unpackShorthandStyles } from 'craft/utils';

export { CssSelectorId } from 'craft/types/cssSelector';

const createSelectorObject = (cssSelectorId: CssSelectorId, cssObj: object) => {
  const obj = getCssSelectorDefinition(cssSelectorId);

  return { ...obj, css: cssObj };
};

/**
 * We had a performance issue where `css` is used inside an object ref. to avoid recomputing
 */
const SELECTOR_EMPTY_OBJ = {};
const CSS_EMPTY_OBJ = {};

Object.freeze(CSS_EMPTY_OBJ);
Object.freeze(SELECTOR_EMPTY_OBJ);

export const useMultiCssSelector = (
  nodeIds: NodeId[],
  cssSelectorId: CssSelectorId,
  commonProperty?: any,
) => {
  const {
    cssSelectors,
    actions: { setCustom },
  } = useMultiNodes(
    nodeIds,
    (node) => ({
      cssSelectors: node?.data?.custom?.cssSelectors ?? {},
    }),
    commonProperty,
  );

  /**
   * Because we protect against empty objects, we need to make sure that we
   */
  const cssSelector = (cssSelectors ?? {})[cssSelectorId] ?? SELECTOR_EMPTY_OBJ;
  const css = useMemo(() => unpackShorthandStyles(cssSelector.css ?? CSS_EMPTY_OBJ), [
    cssSelector.css,
  ]);

  const setCssSelector = (
    newCss: object,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
  ) => {
    const newCssObject = pickBy({ ...css, ...(newCss ?? {}) }, (p) => p !== undefined && p !== '');
    const newCssSelector =
      Object.keys(newCssObject).length < 1
        ? null
        : { ...createSelectorObject(cssSelectorId, newCssObject) };

    // make sure that we update our refs so that the return doesn't go out of sync
    const newCssSelectors = pickBy({
      ...(cssSelectors ?? {}),
      [cssSelectorId]: newCssSelector,
    });
    setCustom({ cssSelectors: newCssSelectors }, historyMode, throttleNumber);
  };
  return { css, cssSelector: cssSelector.current, setCssSelector };
};
